import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';

export interface LanguageOption {
  value: string;
  name: string;
  label: string;
}

export interface CountryConfig {
  id: number;
  value: string;
  class: string;
  name: string;
  defaultLanguage: string;
  languages: LanguageOption[];
}

export interface LocaleState {
  country: string;
  language: string;
  langCode: string;
  languages: LanguageOption[];
}

export const SUPPORTED_COUNTRIES: CountryConfig[] = [
  {
    id: 1,
    value: 'NL',
    class: 'fi fi-nl',
    name: 'Nederland',
    defaultLanguage: 'nl',
    languages: [{ value: 'nl', name: 'NL', label: 'Nederlands' }]
  },
  {
    id: 2,
    value: 'BE',
    class: 'fi fi-be',
    name: 'België',
    defaultLanguage: 'nl',
    languages: [
      { value: 'nl', name: 'NL', label: 'Nederlands' },
      { value: 'fr', name: 'FR', label: 'Français' }
    ]
  },
  {
    id: 3,
    value: 'FR',
    class: 'fi fi-fr',
    name: 'Frankrijk',
    defaultLanguage: 'fr',
    languages: [{ value: 'fr', name: 'FR', label: 'Français' }]
  },
  {
    id: 4,
    value: 'DE',
    class: 'fi fi-de',
    name: 'Duitsland',
    defaultLanguage: 'de',
    languages: [{ value: 'de', name: 'DE', label: 'Deutsch' }]
  }
];

const USER_COUNTRY_COOKIE = 'user_selected_country';
const USER_LANG_COOKIE = 'user_selected_lang';
const LEGACY_LANG_COOKIE = 'lang';

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private localeStateSubject: BehaviorSubject<LocaleState>;
  public localeState$: Observable<LocaleState>;

  private countries: CountryConfig[] = SUPPORTED_COUNTRIES;
  private isDetectingIp = false;
  private countryOverridden = false;

  constructor(
    private translateService: TranslateService,
    private cookieService: CookieService,
    @Inject(DOCUMENT) private document: Document
  ) {
    const initialState = this.resolveInitialLocale();
    this.localeStateSubject = new BehaviorSubject<LocaleState>(initialState);
    this.localeState$ = this.localeStateSubject.asObservable();

    this.applyLocale(initialState, false);

    // If neither URL param nor explicit user cookie specified a country, perform async IP detection
    if (!this.hasExplicitCountryOverride()) {
      this.detectCountryByIp();
    }
  }

  public get currentLocale(): LocaleState {
    return this.localeStateSubject.value;
  }

  public get supportedCountries(): CountryConfig[] {
    return this.countries;
  }

  public getCountryConfig(countryCode: string): CountryConfig {
    return (
      this.countries.find((c) => c.value.toUpperCase() === countryCode.toUpperCase()) ||
      this.countries[0]
    );
  }

  public getCountryCodeById(countryId: number): string | null {
    const found = this.countries.find((c) => c.id === Number(countryId));
    return found ? found.value : null;
  }

  /**
   * Set user's country
   * @param countryCode 'BE', 'NL', 'FR', 'DE'
   * @param isExplicitUserAction true if triggered by user clicking the dropdown
   * @param preferredLanguage optional preferred language code (e.g. from campaign)
   */
  public setCountry(
    countryCode: string,
    isExplicitUserAction: boolean = true,
    preferredLanguage?: string
  ): void {
    this.countryOverridden = true;
    const targetCountry = this.getCountryConfig(countryCode);
    const country = targetCountry.value;

    let language: string;
    const currentLang = this.currentLocale.language;

    if (preferredLanguage && targetCountry.languages.some((l) => l.value === preferredLanguage)) {
      language = preferredLanguage;
    } else if (targetCountry.languages.some((l) => l.value === currentLang)) {
      language = currentLang;
    } else {
      // Choose best language for target country based on browser preferences
      language = this.resolveBestLanguageForCountry(country);
    }

    const langCode = `${language}-${country}`;
    const newState: LocaleState = {
      country,
      language,
      langCode,
      languages: targetCountry.languages
    };

    if (isExplicitUserAction) {
      this.cookieService.set(USER_COUNTRY_COOKIE, country, 365, '/');
      this.cookieService.set(USER_LANG_COOKIE, language, 365, '/');
    }

    this.applyLocale(newState, true);
  }

  /**
   * Set user's language
   * @param languageCode 'nl', 'fr', 'de'
   * @param isExplicitUserAction true if triggered by user clicking the dropdown
   */
  public setLanguage(languageCode: string, isExplicitUserAction: boolean = true): void {
    const country = this.currentLocale.country;
    const targetCountry = this.getCountryConfig(country);

    // Validate that language is supported for current country
    const isSupported = targetCountry.languages.some((l) => l.value === languageCode);
    const language = isSupported ? languageCode : targetCountry.defaultLanguage;
    const langCode = `${language}-${country}`;

    const newState: LocaleState = {
      country,
      language,
      langCode,
      languages: targetCountry.languages
    };

    if (isExplicitUserAction) {
      this.cookieService.set(USER_LANG_COOKIE, language, 365, '/');
    }

    this.applyLocale(newState, true);
  }

  /**
   * Parse language and country from query param if available
   */
  public setLocaleFromParam(paramLang: string): void {
    if (!paramLang) return;

    const parsed = this.parseLocaleCode(paramLang);
    if (parsed) {
      this.countryOverridden = true;
      const targetCountry = this.getCountryConfig(parsed.country);
      const isSupportedLang = targetCountry.languages.some((l) => l.value === parsed.language);
      const language = isSupportedLang ? parsed.language : targetCountry.defaultLanguage;

      const newState: LocaleState = {
        country: targetCountry.value,
        language,
        langCode: `${language}-${targetCountry.value}`,
        languages: targetCountry.languages
      };

      this.applyLocale(newState, true);
    }
  }

  private applyLocale(state: LocaleState, emit: boolean): void {
    // Sync with ngx-translate
    this.translateService.setFallbackLang(state.language);
    this.translateService.use(state.language);

    if (this.document && this.document.documentElement) {
      this.document.documentElement.lang = state.language;
    }

    // Keep legacy cookie synced for backward compatibility
    this.cookieService.set(LEGACY_LANG_COOKIE, state.langCode, 365, '/');

    if (emit) {
      this.localeStateSubject.next(state);
    }
  }

  private hasExplicitCountryOverride(): boolean {
    const urlLang = this.getParamFromUrl('lang');
    if (urlLang && this.parseLocaleCode(urlLang)) {
      return true;
    }
    if (this.cookieService.check(USER_COUNTRY_COOKIE)) {
      return true;
    }
    return false;
  }

  private resolveInitialLocale(): LocaleState {
    // 1. Check URL param ?lang=xx-XX
    const urlLang = this.getParamFromUrl('lang');
    if (urlLang) {
      const parsed = this.parseLocaleCode(urlLang);
      if (parsed) {
        const countryConfig = this.getCountryConfig(parsed.country);
        const language = countryConfig.languages.some((l) => l.value === parsed.language)
          ? parsed.language
          : countryConfig.defaultLanguage;
        return {
          country: countryConfig.value,
          language,
          langCode: `${language}-${countryConfig.value}`,
          languages: countryConfig.languages
        };
      }
    }

    // 2. Check explicit user saved preferences
    if (this.cookieService.check(USER_COUNTRY_COOKIE)) {
      const savedCountry = this.cookieService.get(USER_COUNTRY_COOKIE);
      const countryConfig = this.getCountryConfig(savedCountry);
      let language = countryConfig.defaultLanguage;

      if (this.cookieService.check(USER_LANG_COOKIE)) {
        const savedLang = this.cookieService.get(USER_LANG_COOKIE);
        if (countryConfig.languages.some((l) => l.value === savedLang)) {
          language = savedLang;
        }
      }

      return {
        country: countryConfig.value,
        language,
        langCode: `${language}-${countryConfig.value}`,
        languages: countryConfig.languages
      };
    }

    // 3. Detect country from browser languages
    const detectedCountry = this.detectCountryFromBrowser();
    const countryConfig = this.getCountryConfig(detectedCountry);
    const language = this.resolveBestLanguageForCountry(countryConfig.value);

    return {
      country: countryConfig.value,
      language,
      langCode: `${language}-${countryConfig.value}`,
      languages: countryConfig.languages
    };
  }

  /**
   * Check navigator.languages for country hints (e.g. 'nl-BE', 'fr-BE', 'nl-NL')
   */
  private detectCountryFromBrowser(): string {
    const langs = this.getBrowserLanguages();

    for (const tag of langs) {
      const upper = tag.toUpperCase();
      if (upper.endsWith('-BE') || upper.includes('-BE-') || upper === 'BE') {
        return 'BE';
      }
      if (upper.endsWith('-NL') || upper.includes('-NL-') || upper === 'NL') {
        return 'NL';
      }
      if (upper.endsWith('-FR') || upper.includes('-FR-') || upper === 'FR') {
        return 'FR';
      }
      if (upper.endsWith('-DE') || upper.includes('-DE-') || upper === 'DE') {
        return 'DE';
      }
    }

    // Secondary hint from language prefix if no region tag was present
    const primaryLang = (langs[0] || '').toLowerCase().slice(0, 2);
    if (primaryLang === 'fr') return 'FR';
    if (primaryLang === 'de') return 'DE';

    return 'NL';
  }

  /**
   * For a given country (e.g. 'BE'), resolve language based on browser preference.
   * If in Belgium: checks if user prefers French over Dutch or Dutch over French.
   */
  public resolveBestLanguageForCountry(countryCode: string): string {
    const config = this.getCountryConfig(countryCode);
    const supported = config.languages.map((l) => l.value);

    if (supported.length === 1) {
      return supported[0];
    }

    const browserLangs = this.getBrowserLanguages();

    for (const tag of browserLangs) {
      const code = tag.toLowerCase().slice(0, 2);
      if (supported.includes(code)) {
        return code;
      }
    }

    return config.defaultLanguage;
  }

  private getBrowserLanguages(): string[] {
    const list: string[] = [];
    if (typeof navigator !== 'undefined') {
      if (navigator.languages && navigator.languages.length) {
        list.push(...navigator.languages);
      } else if (navigator.language) {
        list.push(navigator.language);
      }
    }
    return list;
  }

  /**
   * Async IP geolocation via lightweight HTTPS endpoint
   */
  private async detectCountryByIp(): Promise<void> {
    if (this.isDetectingIp) return;
    this.isDetectingIp = true;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1500);

      const response = await fetch('https://api.country.is/', {
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        const detectedCountryCode = (data && data.country ? data.country : '').toUpperCase();

        if (detectedCountryCode && this.countries.some((c) => c.value === detectedCountryCode)) {
          // If detected country is different from current state and user/campaign has not overridden country
          if (
            detectedCountryCode !== this.currentLocale.country &&
            !this.countryOverridden &&
            !this.hasExplicitCountryOverride()
          ) {
            this.setCountry(detectedCountryCode, false);
          }
        }
      }
    } catch (e) {
      // Non-blocking: fallback to browser language/default already applied
    } finally {
      this.isDetectingIp = false;
    }
  }

  private parseLocaleCode(locale: string): { language: string; country: string } | null {
    if (!locale) return null;
    const parts = locale.split('-');
    if (parts.length >= 2) {
      return {
        language: parts[0].toLowerCase(),
        country: parts[1].toUpperCase()
      };
    }
    return null;
  }

  private getParamFromUrl(param: string): string | null {
    if (typeof window !== 'undefined' && window.location && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      return params.get(param);
    }
    return null;
  }
}
